import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Gift, Plus, Calendar, DollarSign, TrendingUp, X, Loader2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from './adminConstants';

const AdminCampaignsTab = () => {
  const { getToken } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '',
    starts_at: '',
    ends_at: '',
    bonus_per_sale: '',
    min_sales: '0',
    budget_cap: '',
  });

  const fetchCampaigns = async () => {
    try {
      const token = getToken();
      const res = await axios.get(`${API}/admin/campaigns`, { headers: { Authorization: `Bearer ${token}` } });
      setCampaigns(res.data.campaigns || []);
    } catch {
      toast.error('Erro ao carregar campanhas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCampaigns(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreate = async () => {
    if (!form.name.trim() || !form.starts_at || !form.ends_at || !form.bonus_per_sale) {
      toast.error('Preencha nome, datas e bonus');
      return;
    }
    setSaving(true);
    try {
      const token = getToken();
      const payload = {
        name: form.name.trim(),
        starts_at: new Date(form.starts_at).toISOString(),
        ends_at: new Date(form.ends_at).toISOString(),
        bonus_per_sale: parseFloat(form.bonus_per_sale),
        min_sales: parseInt(form.min_sales) || 0,
        budget_cap: parseFloat(form.budget_cap) || 0,
      };
      await axios.post(`${API}/admin/campaigns`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Campanha criada! Bonus ja estao sendo aplicados em novas vendas.');
      setShowForm(false);
      setForm({ name: '', starts_at: '', ends_at: '', bonus_per_sale: '', min_sales: '0', budget_cap: '' });
      fetchCampaigns();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao criar campanha');
    } finally {
      setSaving(false);
    }
  };

  const handleEnd = async (campaignId, name) => {
    if (!window.confirm(`Encerrar campanha "${name}"?\n\nNovas vendas NAO receberao mais o bonus desta campanha.`)) return;
    try {
      const token = getToken();
      await axios.post(`${API}/admin/campaigns/${campaignId}/end`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Campanha encerrada');
      fetchCampaigns();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro');
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#C5A059]" /></div>;

  return (
    <div className="space-y-4">
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-3 flex flex-row items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Gift className="w-5 h-5 text-[#C5A059]" />
              Campanhas de Bonus
            </CardTitle>
            <CardDescription className="text-xs">Ofereca bonus extras aos afiliados em periodos especificos.</CardDescription>
          </div>
          <Button onClick={() => setShowForm(v => !v)} className="bg-[#C5A059] hover:bg-[#b8933f] text-[#0A2647] h-9 px-4 text-sm font-bold" data-testid="new-campaign-btn">
            {showForm ? <><X className="w-3.5 h-3.5 mr-1.5" />Cancelar</> : <><Plus className="w-3.5 h-3.5 mr-1.5" />Nova Campanha</>}
          </Button>
        </CardHeader>
        {showForm && (
          <CardContent className="border-t bg-amber-50/30 pt-4 space-y-3">
            <div>
              <Label className="text-xs">Nome da Campanha</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex: Blitz Dezembro" className="h-9 mt-1" data-testid="campaign-name" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Inicio</Label>
                <Input type="datetime-local" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} className="h-9 mt-1" data-testid="campaign-starts" />
              </div>
              <div>
                <Label className="text-xs">Fim</Label>
                <Input type="datetime-local" value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} className="h-9 mt-1" data-testid="campaign-ends" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Bonus por Venda (R$)</Label>
                <Input type="number" step="0.01" min="0" value={form.bonus_per_sale} onChange={(e) => setForm({ ...form, bonus_per_sale: e.target.value })} placeholder="15.00" className="h-9 mt-1" data-testid="campaign-bonus" />
              </div>
              <div>
                <Label className="text-xs">Min. Vendas (0 = sem minimo)</Label>
                <Input type="number" min="0" value={form.min_sales} onChange={(e) => setForm({ ...form, min_sales: e.target.value })} className="h-9 mt-1" data-testid="campaign-min-sales" />
              </div>
              <div>
                <Label className="text-xs">Teto Total (R$, opcional)</Label>
                <Input type="number" step="0.01" min="0" value={form.budget_cap} onChange={(e) => setForm({ ...form, budget_cap: e.target.value })} placeholder="1000.00" className="h-9 mt-1" data-testid="campaign-budget" />
              </div>
            </div>
            <div className="flex items-center gap-2 pt-2">
              <Button onClick={handleCreate} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white h-9" data-testid="save-campaign">
                {saving && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}Criar Campanha
              </Button>
              <p className="text-[10px] text-slate-500">💡 Apos criada, nao pode ser editada (somente encerrada).</p>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Lista de campanhas */}
      {campaigns.length === 0 ? (
        <Card className="border-dashed"><CardContent className="p-6 text-center text-sm text-slate-500">Nenhuma campanha criada ainda.</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {campaigns.map(c => {
            const now = new Date();
            const start = new Date(c.starts_at);
            const end = new Date(c.ends_at);
            const isActive = c.status === 'active' && now >= start && now <= end;
            const isFuture = c.status === 'active' && now < start;
            const isEnded = c.status !== 'active' || now > end;
            const budgetUsedPct = c.budget_cap ? Math.min(100, (c.total_paid_bonus / c.budget_cap) * 100) : 0;
            return (
              <Card key={c.id} className={`border-0 shadow-sm ${isActive ? 'bg-gradient-to-br from-emerald-50 to-white ring-1 ring-emerald-300' : ''}`} data-testid={`campaign-${c.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div>
                      <p className="font-bold text-slate-800 text-sm">{c.name}</p>
                      <p className="text-[11px] text-slate-500 flex items-center gap-1 mt-0.5"><Calendar className="w-3 h-3" />{start.toLocaleDateString('pt-BR')} — {end.toLocaleDateString('pt-BR')}</p>
                    </div>
                    {isActive && <span className="text-[9px] font-bold text-emerald-700 bg-emerald-100 px-2 py-1 rounded-full uppercase">Ativa</span>}
                    {isFuture && <span className="text-[9px] font-bold text-blue-700 bg-blue-100 px-2 py-1 rounded-full uppercase">Agendada</span>}
                    {isEnded && <span className="text-[9px] font-bold text-slate-600 bg-slate-100 px-2 py-1 rounded-full uppercase">{c.status === 'ended' ? 'Encerrada' : 'Expirada'}</span>}
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="bg-white border rounded-lg p-2">
                      <p className="text-[9px] text-slate-500 uppercase tracking-wider">Bonus/Venda</p>
                      <p className="text-sm font-bold text-[#C5A059]">R$ {Number(c.bonus_per_sale).toFixed(2)}</p>
                    </div>
                    <div className="bg-white border rounded-lg p-2">
                      <p className="text-[9px] text-slate-500 uppercase tracking-wider">Vendas</p>
                      <p className="text-sm font-bold text-slate-800">{c.total_sales_counted || 0}</p>
                    </div>
                    <div className="bg-white border rounded-lg p-2">
                      <p className="text-[9px] text-slate-500 uppercase tracking-wider">Pago</p>
                      <p className="text-sm font-bold text-emerald-600">R$ {Number(c.total_paid_bonus || 0).toFixed(2)}</p>
                    </div>
                  </div>
                  {c.budget_cap && (
                    <div className="mt-3">
                      <div className="flex items-center justify-between text-[10px] mb-1">
                        <span className="text-slate-500">Teto: R$ {c.budget_cap}</span>
                        <span className="text-slate-500">{budgetUsedPct.toFixed(0)}% usado</span>
                      </div>
                      <div className="h-1.5 bg-slate-200 rounded-full"><div className={`h-full rounded-full ${budgetUsedPct >= 80 ? 'bg-red-500' : 'bg-emerald-500'}`} style={{ width: `${budgetUsedPct}%` }} /></div>
                    </div>
                  )}
                  {isActive && (
                    <button onClick={() => handleEnd(c.id, c.name)} className="mt-3 text-[11px] text-red-600 hover:text-red-700 font-semibold" data-testid={`end-campaign-${c.id}`}>
                      Encerrar agora
                    </button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Card className="border-0 bg-slate-50 shadow-sm">
        <CardContent className="p-4 flex items-start gap-3">
          <TrendingUp className="w-4 h-4 text-slate-600 shrink-0 mt-0.5" />
          <div className="text-[11px] text-slate-600 leading-relaxed space-y-1">
            <p><strong>Como funciona:</strong> cada venda realizada no periodo da campanha soma o bonus ao valor normal da comissao do afiliado.</p>
            <p><strong>Exemplo:</strong> Bonus R$15 + comissao normal Pro R$50 = afiliado recebe R$65 naquela venda.</p>
            <p><strong>Teto de gasto:</strong> quando atingido, novas vendas nao recebem mais o bonus (protege orcamento).</p>
            <p><strong>Nao afeta</strong> comissoes ja pagas antes da campanha.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminCampaignsTab;
